import { Root } from "mdast";
import { Plugin } from "unified";
import {
  ComponentRegistry,
  createCustomElement,
  createTextVisitor,
  getCompleteTagPattern,
  getIncompleteTagPattern,
  parseAttributes,
  processTextWithRegex,
  ReplacementResult,
} from "./utils";

// Custom plugin to convert registered component tags from text to elements
export const parseRegistryElements: Plugin<
  [{ registry: ComponentRegistry }],
  Root
> = (options) => {
  const registryTags = Object.keys(options?.registry || {});

  if (registryTags.length === 0) {
    return () => {}; // Return no-op transformer
  }

  const incompleteTagPattern = getIncompleteTagPattern(registryTags);
  const completeTagPattern = getCompleteTagPattern(registryTags);

  // Return the transformer function that uses pre-compiled regexes
  return (tree: Root) => {
    const visitText = createTextVisitor();

    // Single pass: Handle both complete and incomplete tags in one traversal
    visitText(tree, (node, index, parent) => {
      if (!node.value) return;

      // Combine both patterns - complete tags take precedence via ordering
      const combinedPattern = `(${completeTagPattern})|(${incompleteTagPattern})`;
      const combinedRegex = new RegExp(combinedPattern, "g");

      return processTextWithRegex(
        node,
        index,
        parent,
        combinedRegex,
        (match): ReplacementResult => {
          const [fullMatch, completeMatch, incompleteMatch] = match;

          if (completeMatch) {
            // Handle complete tag (group 1)
            const tagMatch = completeMatch.match(/<(\w+)(?:\s([^>]*))?\s*\/>/);
            if (!tagMatch) {
              return {
                node: { type: "text", value: completeMatch },
              };
            }

            const [, tagName, attributesStr] = tagMatch;
            const properties = parseAttributes(attributesStr || "");

            return {
              node: createCustomElement(tagName, properties),
            };
          } else if (incompleteMatch) {
            // Handle incomplete tag (group 2)
            const tagNameMatch = incompleteMatch.match(
              /^<([a-zA-Z][a-zA-Z0-9_]*)/
            );
            if (!tagNameMatch) {
              return {
                node: { type: "text", value: incompleteMatch },
              };
            }

            const tagName = tagNameMatch[1];
            return {
              node: createCustomElement("placeholder", { tagName }),
            };
          }

          // Fallback
          return {
            node: { type: "text", value: fullMatch },
          };
        }
      );
    });
  };
};
